import React from 'react';
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { useLocation, useNavigate } from 'react-router';

import { colors } from '../../../theme/tokens';
import type { SidebarNavItem } from './useSidebarNav';

const ACTIVE_BG = colors.brand;
const CHILD_ACTIVE_BG = `${colors.brand}40`;

const isActive = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

/**
 * Which sibling a URL actually belongs to — the LONGEST match wins.
 *
 * `isActive` is a prefix test, so a child whose path is a prefix of another's
 * matches too: on `…/flex-components/palette` both "Components"
 * (`…/flex-components`) and "Palette" highlight, and the group reads as if two
 * pages were open at once. Comparing against the longest matching sibling picks
 * the one the user is actually on. Returns 0 when nothing matches, which no
 * real path length can equal.
 */
const longestActivePath = (pathname: string, children: Array<{ path: string }>): number =>
  children.reduce((best, c) => (isActive(pathname, c.path) ? Math.max(best, c.path.length) : best), 0);

interface Props {
  item: SidebarNavItem;
  open: boolean;
  onToggle: () => void;
  /**
   * False when another group owns the active page. A path can appear in two
   * groups (`sidebar.alsoIn`), and without this both would match the pathname
   * and both would render as active. Defaults true so every other caller and
   * every existing test keeps today's behaviour.
   */
  isActiveGroup?: boolean;
  /** Lets the sidebar remember which group the user reached a page through. */
  onChildNavigate?: (path: string) => void;
}

const SidebarItem: React.FC<Props> = ({ item, open, onToggle, isActiveGroup = true, onChildNavigate }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (item.type === 'expandable') {
    const childActive = isActiveGroup && item.children.some((c) => isActive(pathname, c.path));
    const activeChildPathLen = longestActivePath(pathname, item.children);

    return (
      <>
        <ListItemButton
          disableRipple
          disableTouchRipple
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`sidebar-group-${item.key}`}
          sx={{
            p: 2,
            borderRadius: '8px',
            backgroundColor: childActive ? ACTIVE_BG : 'transparent',
            color: colors.textPrimary,
            gap: 1,
            height: 48,
            '&:hover': { backgroundColor: childActive ? ACTIVE_BG : 'rgba(0,0,0,0.03)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 24 }}>
            <item.icon sx={{ width: 24, height: 24, color: colors.textPrimary }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography sx={{ fontSize: 16, fontWeight: 600 }}>{item.label}</Typography>}
          />
          <ExpandMore
            sx={{
              width: 24,
              height: 24,
              transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.2s',
              color: colors.textSecondary,
            }}
          />
        </ListItemButton>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List id={`sidebar-group-${item.key}`} sx={{ p: 0 }}>
            {item.children.map((child) => {
              // Most-specific sibling wins — see longestActivePath. And only the
              // group that owns the page highlights, so an `alsoIn` echo of this
              // same path in another group stays quiet.
              const active = isActiveGroup
                && isActive(pathname, child.path)
                && child.path.length === activeChildPathLen;
              return (
                <ListItemButton
                  key={child.path}
                  disableRipple
                  disableTouchRipple
                  onClick={() => { onChildNavigate?.(child.path); navigate(child.path); }}
                  sx={{
                    px: 6,
                    py: 2,
                    mt: 1,
                    borderRadius: '8px',
                    height: 48,
                    backgroundColor: active ? CHILD_ACTIVE_BG : 'transparent',
                    color: active ? colors.textPrimary : colors.textSecondary,
                    '&:hover': {
                      backgroundColor: active ? CHILD_ACTIVE_BG : 'rgba(0,0,0,0.03)',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: 16, fontWeight: active ? 600 : 500 }}>
                        {child.label}
                      </Typography>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  // Leaf (internal path or external URL)
  const isExternal = !!item.externalUrl;
  const active = !isExternal && item.path ? isActive(pathname, item.path) : false;

  return (
    <ListItemButton
      disableRipple
      disableTouchRipple
      onClick={() => {
        if (isExternal) {
          window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
        } else if (item.path) {
          navigate(item.path);
        }
      }}
      sx={{
        p: 2,
        borderRadius: '8px',
        backgroundColor: active ? ACTIVE_BG : 'transparent',
        color: colors.textPrimary,
        fontWeight: active ? 600 : 500,
        height: 48,
        gap: 1,
        '&:hover': { backgroundColor: active ? ACTIVE_BG : 'rgba(0,0,0,0.03)' },
      }}
    >
      <ListItemIcon sx={{ minWidth: 24 }}>
        <item.icon sx={{ width: 24, height: 24, color: colors.textPrimary }} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography sx={{ fontSize: 16, fontWeight: active ? 600 : 500 }}>
            {item.label}
          </Typography>
        }
      />
      {isExternal && (
        <Box component="span" sx={{ ml: 'auto', display: 'inline-flex' }}>
          <OpenInNew sx={{ width: 16, height: 16, color: colors.textTertiary }} />
        </Box>
      )}
    </ListItemButton>
  );
};

export default SidebarItem;
